import { ComponentType } from 'react';
import { FormBlockInstance } from '../api-types';

/** What every block renderer receives. Nothing about navigation or saving leaks in here. */
export interface BlockRendererProps {
  block: FormBlockInstance;
  /** This block's answer, in whatever shape its type defines. Undefined when untouched. */
  answer: unknown;
  /** Report a new answer. The runner owns autosave; a renderer only says what changed. */
  onChange: (answer: unknown) => void;
  /** The locale to read labels in — already resolved against the definition's languages. */
  locale: string;
  /**
   * Say that this block is mid-request and the respondent must not leave or submit yet.
   *
   * Optional, and only for a block that talks to the server itself (the file upload). Its own
   * `answer` stays at the old value until the request settles, so the runner's Next and Submit
   * would otherwise stay enabled on a stale answer and let someone walk away from a request whose
   * result is then dropped. The runner locks navigation while it is true; a block that sets it
   * clears it again when it unmounts.
   */
  onBusyChange?: (busy: boolean) => void;
  /**
   * This question was not released when an operator reopened the response, so its saved answer is shown and cannot be changed.
   *
   * Optional, and read only by the block whose actions do not go through `onChange`: the file upload talks to the server itself,
   * so the runner's guard on `onChange` cannot stop it. Every other renderer is frozen from outside by the runner (a disabled
   * fieldset and no pointer events around it) and can ignore this.
   */
  readOnly?: boolean;
}

/**
 * One entry in the catalog the app can render.
 *
 * `isAnswered` lives here rather than in the runner because only the type knows what "answered"
 * means for its own shape — a single select needs a chosen key, a rating needs a number, a grid
 * needs at least one cell. It receives the block as well as the answer because that judgment can
 * depend on configuration: an "other" choice is only answered once text has been typed, and which
 * option is the "other" one is in the config. Keeping this in the entry is what lets the runner
 * compute progress and gate Next while knowing nothing about any block type.
 *
 * The frontend deliberately does NOT decide validity. `isAnswered` exists to give immediate
 * feedback and to stop someone walking into a submit the server will refuse; the server validates
 * everything again and its verdict wins.
 */
export interface BlockRendererEntry {
  type: string;
  typeVersion: number;
  /** INPUT collects an answer; CONTENT only reads (a statement, a divider). */
  kind: 'INPUT' | 'CONTENT';
  component: ComponentType<BlockRendererProps>;
  isAnswered: (answer: unknown, block: FormBlockInstance) => boolean;
}

/**
 * The id the block's heading carries, and the one its control points `aria-labelledby` at.
 *
 * Part of the renderer contract rather than of any renderer: the heading and the control it names
 * are written in two different files, and a string built twice is a string that eventually differs.
 * A dangling `aria-labelledby` leaves a control announced as unlabelled, which is silent — nothing
 * looks wrong until somebody using a screen reader meets a question with no words.
 */
export const questionIdOf = (block: FormBlockInstance): string =>
  `form-question-${block.instanceId}`;
